import { QueryResult } from '@apollo/client';
import { apiClient } from 'axios-client';
import {
  CityProViewSetInput,
  Exact,
  GoogleReviewLinkInsertInput,
  GoogleReviewLinkSetInput,
  ProCertificationSetInput,
  ProFormUpdateQuery,
  ProMediaObjectInsertInput,
  ProMediaObjectSetInput,
  ProMessageInsertInput,
  ProMessageSetInput,
  ProPostalAddressInsertInput,
  ProPresentationSetInput,
  ProPriceInsertInput,
  ProViewProPersonSetInput,
  ProViewProViewSetInput,
  ProViewSetInput,
  TradeProPresentationInsertInput,
  TradeProViewSetInput,
  useProFormCreateGoogleReviewLinkMutation,
  useProFormCreateSlugRedirectMutation,
  useProFormUpdateFetchCompanyLazyQuery,
  useProFormUpdateGoogleReviewLinkMutation,
  useProFormUpdateMutation,
  useProFormUpdateProMediaObjectMutation,
  useProFormUpdateProMessageMutation,
  useProFormUpdateProPriceMutation,
  useProFormUpdateProViewGoogleReviewLinkMutation,
  useProFormUpdateProViewProPersonMutation,
  useProFormUpdateQueryLazyQuery,
  useUpdatePresentationMutation,
} from 'generated/graphql';
import React, { FunctionComponent, useCallback } from 'react';
import ProFormUpdateContainer from './pro-form-update-container';

export type ProFormUpdateProParams = {
  insertProPostalAddressOne: ProPostalAddressInsertInput;
  insertProMediaObjects: ProMediaObjectInsertInput[];
  deleteProMediaObjects: number[];
  insertProPrices: ProPriceInsertInput[];
  deleteProPrices: number[];
  deleteGoogleReviewLinks: number[];
  insertProMessages: ProMessageInsertInput[];
  deleteProMessages: number[];
  proPresentationId: number;
  proView: ProViewSetInput;
  insertTrades: TradeProViewSetInput[];
  deleteTrades: number[];
  insertCities: CityProViewSetInput[];
  deleteCities: number[];
  insertCertifications: ProCertificationSetInput[];
  deleteCertifications: number[];
  insertColleagues: ProViewProViewSetInput[];
  deleteColleagues: number[];
  deleteProPeople: number[];
  deleteAccounts: number[];
  insertMainTrades: TradeProPresentationInsertInput[];
  deleteMainTrades: number[];
};

type ProFormUpdateRepositoryProps = {
  proViewId: number;
  proPresentationId: number;
};

const ProFormUpdateRepository: FunctionComponent<
  ProFormUpdateRepositoryProps
> = ({ proViewId, proPresentationId }) => {
  const [proFormUpdateQuery, { data }] = useProFormUpdateQueryLazyQuery();

  const [fetchCompanyQuery] = useProFormUpdateFetchCompanyLazyQuery();

  const fetchCompany = useCallback(
    (companyId: number) => fetchCompanyQuery({ variables: { id: companyId } }),
    [],
  );

  const fetchProForm = useCallback(async (): Promise<
    QueryResult<
      ProFormUpdateQuery,
      Exact<{
        id: number;
      }>
    >
  > => {
    return proFormUpdateQuery({
      variables: {
        id: proViewId,
      },
    });
  }, [proViewId]);

  const [updateGoogleReviewLinkMutation] =
    useProFormUpdateGoogleReviewLinkMutation();

  const updateGoogleReviewLink = useCallback(
    async (
      id: number,
      googleReviewLink: GoogleReviewLinkSetInput,
    ): Promise<void> => {
      await updateGoogleReviewLinkMutation({
        variables: {
          googleReviewLinkId: id,
          googleReviewLink,
        },
      });
    },
    [],
  );

  const [updateProViewGoogleReviewLinkMutation] =
    useProFormUpdateProViewGoogleReviewLinkMutation();

  const updateProViewGoogleReviewLink = useCallback(
    async (proViewId: number, googleReviewLinkId: number): Promise<void> => {
      await updateProViewGoogleReviewLinkMutation({
        variables: {
          googleReviewLinkId,
          proViewId,
        },
      });
    },
    [],
  );

  const [updateProMessageMutation] = useProFormUpdateProMessageMutation();

  const updateProMessage = useCallback(
    async (id: number, proMessage: ProMessageSetInput): Promise<void> => {
      await updateProMessageMutation({
        variables: {
          proMessageId: id,
          proMessage,
        },
      });
    },
    [],
  );

  const updateProIntervention = useCallback(
    async (proViewId: number): Promise<void> => {
      return await apiClient.post('/form/pro-intervention/update', {
        proViewId,
      });
    },
    [],
  );

  const [updateProPriceMutation] = useProFormUpdateProPriceMutation();

  const updateProPrice = useCallback(
    async (id: number, proPrice: ProPriceInsertInput): Promise<void> => {
      await updateProPriceMutation({
        variables: {
          proPriceId: id,
          proPrice,
        },
      });
    },
    [],
  );

  const [updateProMediaObjectMutation] =
    useProFormUpdateProMediaObjectMutation();

  const updateProMediaObject = useCallback(
    async (
      id: number,
      proMediaObject: ProMediaObjectSetInput,
    ): Promise<void> => {
      await updateProMediaObjectMutation({
        variables: {
          proMediaObjectId: id,
          proMediaObject,
        },
      });
    },
    [],
  );

  const [updateProViewProPersonMutation] =
    useProFormUpdateProViewProPersonMutation();

  const updateProViewProPerson = useCallback(
    async (
      proPersonId: number,
      proViewId: number,
      proViewProPerson: ProViewProPersonSetInput,
    ): Promise<void> => {
      await updateProViewProPersonMutation({
        variables: {
          proPersonId,
          proViewId,
          proViewProPerson,
        },
      });
    },
    [],
  );

  const [updateProMutation] = useProFormUpdateMutation();

  const updatePro = useCallback(
    async (variables: ProFormUpdateProParams) => {
      await updateProMutation({
        variables: {
          insertProMediaObjects: variables.insertProMediaObjects,
          insertProPrices: variables.insertProPrices,
          deleteProPrices: variables.deleteProPrices,
          deleteGoogleReviewLinks: variables.deleteGoogleReviewLinks,
          insertProMessages: variables.insertProMessages,
          proPresentationId: variables.proPresentationId,
          proViewId: proViewId,
          proView: variables.proView,
          insertProPostalAddressOne: variables.insertProPostalAddressOne,
          deleteProMediaObjects: variables.deleteProMediaObjects,
          deleteProMessages: variables.deleteProMessages,
          insertTrades: variables.insertTrades,
          deleteTrades: variables.deleteTrades,
          insertCities: variables.insertCities,
          insertCertifications: variables.insertCertifications,
          deleteCertifications: variables.deleteCertifications,
          insertColleagues: variables.insertColleagues,
          deleteCities: variables.deleteCities,
          deleteColleagues: variables.deleteColleagues,
          deleteProPeople: variables.deleteProPeople,
          deleteAccounts: variables.deleteAccounts,
          insertMainTrades: variables.insertMainTrades,
          deleteMainTrades: variables.deleteMainTrades,
        },
      });
    },
    [proViewId],
  );

  const [updatePresentationMutation] = useUpdatePresentationMutation();

  const updatePresentation = useCallback(
    async (variables: {
      id: number;
      presentation: ProPresentationSetInput;
    }): Promise<void> => {
      await updatePresentationMutation({ variables });
    },
    [],
  );

  const [createSlugRedirectMutation] = useProFormCreateSlugRedirectMutation();

  const createSlugRedirect = useCallback(
    async (fromSlug: string, toSlug: string) => {
      await createSlugRedirectMutation({
        variables: {
          input: {
            fromslug: fromSlug,
            toslug: toSlug,
            slugtype: 2,
          },
        },
      });
    },
    [],
  );

  const [createGoogleReviewLinkMutation] =
    useProFormCreateGoogleReviewLinkMutation();

  const moveContract = useCallback(
    async (oldCompanyId: number, newCompanyId: number, proViewId: number) => {
      const result = await apiClient.get(
        `/attachment/pro-documents-informations?pro_organization_id=${oldCompanyId}`,
      );

      const contractKey = result.data.find(
        (document) =>
          document.key.includes(`contrat-${proViewId}.pdf`) &&
          document.metaData?.type === 'contract',
      )?.key;

      if (contractKey) {
        await apiClient.post('/attachment/move', {
          bucket: 'bilik',
          fromKey: contractKey,
          toKey: contractKey.replace(`/${oldCompanyId}/`, `/${newCompanyId}/`),
        });
      } else {
        console.warn('No contract to move');
      }
    },
    [],
  );

  const createGoogleReviewLink = useCallback(
    async (googleReviewLink: GoogleReviewLinkInsertInput) => {
      const { data } = await createGoogleReviewLinkMutation({
        variables: {
          input: {
            url: googleReviewLink.url,
            proPresentationId: googleReviewLink.proPresentationId,
          },
        },
      });

      return data?.insertGoogleReviewLinkOne?.id;
    },
    [],
  );
  return (
    <ProFormUpdateContainer
      proView={data?.proViewByPk}
      proPresentationId={proPresentationId}
      updateGoogleReviewLink={updateGoogleReviewLink}
      updateProViewGoogleReviewLink={updateProViewGoogleReviewLink}
      updateProMediaObject={updateProMediaObject}
      updateProMessage={updateProMessage}
      updateProPrice={updateProPrice}
      updateProIntervention={updateProIntervention}
      updateProViewProPerson={updateProViewProPerson}
      updatePro={updatePro}
      updatePresentation={updatePresentation}
      fetchProForm={fetchProForm}
      createSlugRedirect={createSlugRedirect}
      createGoogleReviewLink={createGoogleReviewLink}
      moveContract={moveContract}
      fetchCompany={fetchCompany}
    />
  );
};

export default ProFormUpdateRepository;
